import { KeyRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getRuntimeConfig } from '@nucleus-commerce/admin-kit/config';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@nucleus-commerce/ui/components/tabs';

import { snippets } from '../generated/snippets';
import { useDocsKeys } from '../hooks/use-docs-keys';
import { useDocsSession } from '../hooks/use-docs-session';

import { CodeBlock } from './code-block';
import type { CodeLanguage } from './code-block-highlighter';
import { fillSnippet, type SnippetValues } from './snippet-tabs-template';

const LANGUAGES = [
  { key: 'curl', language: 'bash' },
  { key: 'fetch', language: 'ts' },
  { key: 'sdk', language: 'ts' },
] as const satisfies Array<{ key: string; language: CodeLanguage }>;

/**
 * curl / fetch / SDK samples of one operation, filled with this deployment's API base URL, the
 * key selected in the key panel and the playground customer's token. Unset values show a
 * placeholder, and a note says where to get a key.
 */
export function SnippetTabs({ operationId }: { operationId: string }) {
  const { t } = useTranslation('docs');
  const { selectedKey } = useDocsKeys();
  const { customer } = useDocsSession();
  const templates = snippets[operationId];
  if (!templates) return null;

  const apiKeyPlaceholder = t('endpoint.snippets.apiKeyPlaceholder');
  const values: SnippetValues = {
    baseUrl: getRuntimeConfig().apiBaseUrl.replace(/\/+$/, ''),
    apiKey: selectedKey?.key ?? apiKeyPlaceholder,
    customerToken: customer?.accessToken ?? t('endpoint.snippets.customerTokenPlaceholder'),
    idempotencyKey: t('endpoint.snippets.idempotencyKeyPlaceholder'),
  };

  return (
    <div className="flex flex-col gap-3">
      <Tabs defaultValue="curl" className="gap-0">
        <TabsList variant="line" aria-label={t('endpoint.snippets.tabs')} className="border-b px-1">
          {LANGUAGES.map(({ key }) => (
            <TabsTrigger key={key} value={key}>
              {t(`endpoint.snippets.${key}`)}
            </TabsTrigger>
          ))}
        </TabsList>
        {LANGUAGES.map(({ key, language }) => (
          <TabsContent key={key} value={key} className="pt-2">
            <CodeBlock code={fillSnippet(templates[key], values)} language={language} />
          </TabsContent>
        ))}
      </Tabs>
      {selectedKey ? null : (
        <p className="text-muted-foreground flex items-center gap-2 text-sm">
          <KeyRound aria-hidden className="size-4 shrink-0" />
          {t('endpoint.snippets.keyUnset', { placeholder: apiKeyPlaceholder })}
        </p>
      )}
    </div>
  );
}
